import { useCallback, useEffect } from 'react';

import { LoadEstimateData } from '../estimate.types';

import { LOCAL_STORAGE_KEY } from '~/core/constants';
import {
  getLocalData,
  removeLocalData,
  setLocalData,
} from '~/core/helpers/storageHelper';
import { useRouterTools } from '~/core/hooks/useRouterTools';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectIsNewEstimate,
  selectLoadEstimateCode,
  updateLoadEstimateCode,
} from '~/redux/slices/estimate';

export const useDefineLoadEstimateCode = () => {
  const dispatch = useAppDispatch();

  const { getSearchParam, updateSearchParam, deleteSearchParam } =
    useRouterTools();
  const loadEstimateCodeFromURL = getSearchParam(
    LOCAL_STORAGE_KEY.LOAD_ESTIMATE
  );
  const loadEstimateCodeFromLocalStorage = getLocalData<string>(
    LOCAL_STORAGE_KEY.LOAD_ESTIMATE
  );
  const loadEstimateCode = useAppSelector(selectLoadEstimateCode);
  const isNewEstimate = useAppSelector(selectIsNewEstimate);
  const keepLoadEstimate = getLocalData<boolean>(
    LOCAL_STORAGE_KEY.KEEP_LOAD_ESTIMATE_IN_URL
  );

  const syncLocalStorageParamWithURLParam =
    useCallback((): LoadEstimateData => {
      setLocalData(LOCAL_STORAGE_KEY.LOAD_ESTIMATE, loadEstimateCodeFromURL);

      return {
        loadedEstimateCode: loadEstimateCodeFromURL as string,
        keepLoadEstimateInURL: true,
      };
    }, [loadEstimateCodeFromURL]);

  const syncURLParamWithLocalStorageParam = useCallback(() => {
    const sanitizedLoadedEstimateCode =
      loadEstimateCodeFromLocalStorage?.replace(/^"|"$/g, '');

    updateSearchParam(
      LOCAL_STORAGE_KEY.LOAD_ESTIMATE,
      sanitizedLoadedEstimateCode as string
    );

    return {
      loadedEstimateCode: sanitizedLoadedEstimateCode,
      keepLoadEstimateInURL: true,
    };
  }, [loadEstimateCodeFromLocalStorage, updateSearchParam]);

  const updateLoadEstimateControlParams = useCallback(
    (
      loadedEstimateCode: string | undefined,
      keepLoadEstimateInURL: boolean
    ) => {
      if (keepLoadEstimateInURL) {
        setLocalData(LOCAL_STORAGE_KEY.KEEP_LOAD_ESTIMATE_IN_URL, true);
      }
      if (loadEstimateCode !== loadedEstimateCode) {
        dispatch(updateLoadEstimateCode(loadedEstimateCode));
      }
    },
    [dispatch, loadEstimateCode]
  );

  const removeLoadEstimateCode = useCallback(() => {
    setLocalData(LOCAL_STORAGE_KEY.KEEP_LOAD_ESTIMATE_IN_URL, false);
    removeLocalData(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);
    deleteSearchParam(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);
  }, [deleteSearchParam]);

  useEffect(() => {
    if (isNewEstimate || !keepLoadEstimate) {
      removeLoadEstimateCode();

      return;
    }

    let keepLoadEstimateInURL = false;
    let loadedEstimateCode;
    let loadEstimateData: LoadEstimateData | undefined;

    if (loadEstimateCodeFromURL) {
      if (loadEstimateCodeFromURL !== loadEstimateCodeFromLocalStorage) {
        loadEstimateData = syncLocalStorageParamWithURLParam();
        keepLoadEstimateInURL = loadEstimateData.keepLoadEstimateInURL;
        loadedEstimateCode = loadEstimateData.loadedEstimateCode;
      }
      if (!loadEstimateCodeFromLocalStorage) {
        setLocalData(LOCAL_STORAGE_KEY.LOAD_ESTIMATE, loadedEstimateCode);
      }
    } else if (loadEstimateCodeFromLocalStorage) {
      loadEstimateData = syncURLParamWithLocalStorageParam();
      keepLoadEstimateInURL = loadEstimateData.keepLoadEstimateInURL;
      loadedEstimateCode = loadEstimateData.loadedEstimateCode as string;
      updateSearchParam(LOCAL_STORAGE_KEY.LOAD_ESTIMATE, loadedEstimateCode);
    }

    if (
      loadEstimateCodeFromURL &&
      loadEstimateCodeFromLocalStorage &&
      loadEstimateCodeFromURL === loadEstimateCodeFromLocalStorage
    ) {
      // Fallback to URL if both are present and equal
      loadedEstimateCode = loadEstimateCodeFromURL;
      keepLoadEstimateInURL = true;
    }

    updateLoadEstimateControlParams(loadedEstimateCode, keepLoadEstimateInURL);
  }, [
    deleteSearchParam,
    dispatch,
    isNewEstimate,
    keepLoadEstimate,
    loadEstimateCode,
    loadEstimateCodeFromLocalStorage,
    loadEstimateCodeFromURL,
    removeLoadEstimateCode,
    syncLocalStorageParamWithURLParam,
    syncURLParamWithLocalStorageParam,
    updateLoadEstimateControlParams,
    updateSearchParam,
  ]);

  return {
    loadEstimateCode,
    removeLoadEstimateCode,
  };
};
